import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, CircularProgress, Alert } from '@mui/material';
import { Typography, Button } from "@mui/material";
import { useNavigate } from "react-router";
import { useAuth } from '../providers/AuthProvider';
import { logError, logInfo } from '../utils/logger';

export const getPricingPlans = async () => {
  const response = await fetch(process.env.REACT_APP_API_BASE_URL + "user/pricing-plans/");
  return response.json();
};

const checkOnboardingStatus = async (token) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_BASE_URL + "user/onboarding/status/",
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    logError('Onboarding status check failed', { error: error.message });
    throw error;
  }
};

const PaidPage = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [isPolling, setIsPolling] = useState(true);
  const [pollCount, setPollCount] = useState(0);
  const [status, setStatus] = useState('checking');
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const auth = useAuth();

  const MAX_POLL_ATTEMPTS = 8; // 8 attempts * 2 seconds = 16 seconds (with 1 extra second buffer)
  const POLL_INTERVAL = 2000; // 2 seconds
  const FALLBACK_TIMEOUT = 15000; // 15 seconds fallback

  const handleSuccessfulOnboarding = useCallback(() => {
    logInfo('Onboarding completed successfully - redirecting to dashboard');
    
    // Force a full app refresh to reload user permissions and data
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 1000);
  }, []);

  const handleTimeout = useCallback(() => {
    setIsPolling(false);
    setStatus('timeout');
    setError('We\'re still processing your payment. Please check back in a few minutes.');
    
    logError('Onboarding status polling timed out', {
      pollCount,
      timeElapsed: timeElapsed / 1000,
      userId: auth.user?.id
    });
  }, [pollCount, timeElapsed, auth.user?.id]);

  const handlePollingError = useCallback((error) => {
    setIsPolling(false);
    setStatus('error');
    setError('There was an issue checking your payment status. Please contact support if this persists.');
    
    logError('Onboarding status polling error', {
      error: error.message,
      pollCount,
      timeElapsed: timeElapsed / 1000,
      userId: auth.user?.id
    });
  }, [pollCount, timeElapsed, auth.user?.id]);

  const pollOnboardingStatus = useCallback(async () => {
    if (!auth.token) {
      setError('Authentication required. Please log in again.');
      setIsPolling(false);
      return;
    }

    try {
      const statusData = await checkOnboardingStatus(auth.token);
      
      logInfo('Onboarding status check result', {
        isComplete: statusData.is_onboarding_complete,
        subscriptionStatus: statusData.subscription_status,
        pollAttempt: pollCount + 1,
        timeElapsed: timeElapsed / 1000
      });

      if (statusData.is_onboarding_complete) {
        setIsPolling(false);
        setStatus('complete');
        handleSuccessfulOnboarding();
        return;
      }

      // Check if we've exceeded max attempts
      if (pollCount >= MAX_POLL_ATTEMPTS - 1) {
        handleTimeout();
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      
    } catch (error) {
      handlePollingError(error);
    }
  }, [auth.token, pollCount, timeElapsed, handleSuccessfulOnboarding, handleTimeout, handlePollingError]);

  useEffect(() => {
    // Initialize pricing plans (kept for backwards compatibility)
    const fetchPricingPlans = async () => {
      try {
        const plans = await getPricingPlans();
        setPricingPlans(plans);
      } catch (error) {
        logError('Failed to fetch pricing plans', { error: error.message });
      }
    };

    fetchPricingPlans();
  }, []);

  useEffect(() => {
    if (!isPolling) return;

    // Start polling immediately
    pollOnboardingStatus();

    // Set up interval for subsequent polls
    const pollInterval = setInterval(() => {
      if (isPolling) {
        pollOnboardingStatus();
      }
    }, POLL_INTERVAL);

    // Set up fallback timeout
    const fallbackTimeout = setTimeout(() => {
      if (isPolling) {
        handleTimeout();
      }
    }, FALLBACK_TIMEOUT);

    // Track time elapsed
    const timeTracker = setInterval(() => {
      setTimeElapsed(prev => prev + 100);
    }, 100);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(fallbackTimeout);
      clearInterval(timeTracker);
    };
  }, [isPolling, pollOnboardingStatus, handleTimeout]);

  const handleManualRedirect = () => {
    logInfo('User manually redirected to dashboard');
    window.location.href = '/dashboard';
  };

  const handleContactSupport = () => {
    // You can implement this to open a support ticket or contact form
    window.open('mailto:support@solosocial.com?subject=Payment Processing Issue', '_blank');
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Thanks for your payment!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We're activating your account...
            </Typography>
            
            <Box sx={{ my: 4 }}>
              <CircularProgress size={60} thickness={4} />
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              This usually takes just a few seconds
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Time elapsed: {Math.round(timeElapsed / 1000)}s
            </Typography>
          </Box>
        );

      case 'complete':
        return (
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom color="success.main">
              ✅ Payment Successful!
            </Typography>
            <Typography variant="body1" paragraph>
              Your account is now active. Redirecting you to the dashboard...
            </Typography>
            <CircularProgress size={40} />
          </Box>
        );

      case 'timeout':
        return (
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Payment Processing
            </Typography>
            <Alert severity="info" sx={{ my: 3, textAlign: 'left' }}>
              Your payment is being processed. This can take up to 5 minutes during high traffic periods.
            </Alert>
            <Typography variant="body1" paragraph>
              You can safely continue to your dashboard, or wait a few minutes and refresh this page.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={handleManualRedirect}
                size="large"
              >
                Continue to Dashboard
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.reload()}
                size="large"
              >
                Check Again
              </Button>
            </Box>
          </Box>
        );

      case 'error':
        return (
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Payment Status
            </Typography>
            <Alert severity="warning" sx={{ my: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={handleManualRedirect}
                size="large"
              >
                Continue to Dashboard
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleContactSupport}
                size="large"
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box 
        sx={{ 
          minHeight: '60vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 4
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          {renderContent()}
        </Box>
      </Box>
    </Container>
  );
};

export default PaidPage;
